import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  PageHeader,
  Space,
  Card,
  Alert,
  Typography,
  Switch,
  Select,
} from 'antd';
import { KeyOutlined, CopyOutlined } from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  MyBreadcrumb,
  accessControls,
} from 'utils';
import { apiKeysCreateService } from 'services/apikeys';
import { message } from 'antd';
import { usersListService } from 'services/user';

const { Text } = Typography;
const { TextArea } = Input;

export default function CreateApiKey() {
  const [form] = Form.useForm();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadPermissions();
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPermissions = () => {
    accessControls.fetch().then((permissions) => {
      setPermissions(permissions);
      // Redirect if user doesn't have permission
      if (!accessControls.can(permissions, 'Client.create:any')) {
        history.push('/apikeys');
      }
    });
  };

  const loadUsers = () => {
    usersListService()
      .then((response) => {
        const usersList = response.data.items.map((item) => ({
          label: item.display_name,
          value: item._id,
        }));
        setUsers(usersList);
      })
      .catch((error) => {
        handleApiError(error);
      });
  };

  const onFinish = (values) => {
    setIsLoading(true);
    apiKeysCreateService(values)
      .then((response) => {
        setIsLoading(false);
        handleApiSuccess(response);
        if (response.data && response.data.apikey) {
          setNewApiKey(response.data.apikey);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const copyApiKey = () => {
    if (newApiKey) {
      navigator.clipboard.writeText(newApiKey);
      message.success('API Key copied to clipboard');
    }
  };

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'API Keys', path: '/apikeys' },
          { label: 'Generate New Key', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/apikeys')}
        title="Generate New API Key"
        subTitle="Create a new API key for integration"
        avatar={{ icon: <KeyOutlined /> }}
      >
        {newApiKey ? (
          <Card>
            <Alert
              message="Important: Save Your API Key"
              description="API key generated successfully. Please copy it now and store it securely."
              type="warning"
              showIcon
            />
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <Text strong>Your new API key:</Text>
              <div
                style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}
              >
                <Input value={newApiKey} readOnly />
                <Button
                  icon={<CopyOutlined />}
                  onClick={copyApiKey}
                  style={{ marginLeft: 10 }}
                >
                  Copy
                </Button>
              </div>
            </div>
            <Button type="primary" onClick={() => history.push('/apikeys')}>
              Return to API Keys List
            </Button>
          </Card>
        ) : (
          <Form
            form={form}
            name="create_api_key"
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ is_active: true }}
          >
            <Form.Item
              name="key_name"
              label="Key Name"
              rules={[
                {
                  required: true,
                  message: 'Key name is required',
                },
                {
                  max: 50,
                  message: 'Key name cannot exceed 50 characters',
                },
              ]}
            >
              <Input placeholder="Enter a name for this API key" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                {
                  required: true,
                  message: 'Description is required',
                },
                {
                  max: 100,
                  message: 'Description cannot exceed 100 characters',
                },
              ]}
            >
              <TextArea placeholder="Enter a description for this API key" />
            </Form.Item>

            <Form.Item
              name="user"
              label="User"
              rules={[
                {
                  required: true,
                  message: 'Please select a user',
                },
              ]}
              tooltip="This API key will follow the access control of the selected user. To modify permissions, update the user's role and access control settings."
            >
              <Select
                placeholder="Select a user"
                style={{ width: '75%' }}
                options={users}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item name="is_active" valuePropName="checked" label="Status">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Generate API Key
                </Button>
                <Button onClick={() => history.push('/apikeys')}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </PageHeader>
    </div>
  );
}
